/**
 * Cliente das rotas /admin. O ÚNICO — não há outro `fetch` em `painel/src`.
 *
 * CAMINHO RELATIVO, SEM BASE DE URL, E ISSO É DESENHO.
 *
 * Não existe `VITE_API_URL` nem base configurável aqui, e a ausência é
 * deliberada: o nginx do contêiner do painel faz proxy de `/admin/` para o
 * serviço da api (ver `painel/nginx.conf.template`). Então toda requisição sai
 * na origem do PAINEL, não na da api, e é o proxy que a encaminha. **Ver o host
 * do painel na aba de rede do navegador é o comportamento correto**, não um
 * sintoma — inclusive nas rotas novas.
 *
 * A razão é o cookie. A sessão é `SameSite=Lax`, e cookie Lax NÃO é enviado em
 * requisição XHR cross-site: um painel em `painel.dominio` chamando
 * `api.dominio` simplesmente não autenticaria. A alternativa seria
 * `SameSite=None; Secure`, que reabre a superfície de CSRF que o Lax fecha.
 *
 * CONSEQUÊNCIA PRÁTICA: embutir um host em qualquer chamada quebra a
 * autenticação de um jeito difícil de ler — 401 em tudo, com cara de sessão
 * expirada. `painel/test/mesmaOrigem.test.tsx` trava o invariante nas duas
 * pontas: no que os componentes emitem em runtime e no que está escrito na
 * fonte.
 *
 * `credentials: 'include'` fica em TODA chamada mesmo assim: ele é o que faz o
 * cookie viajar no `vite dev` e em qualquer topologia sem o proxy.
 */

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }

  get naoAutenticado(): boolean {
    return this.status === 401;
  }

  get semPermissao(): boolean {
    return this.status === 403;
  }
}

async function pedir<T>(metodo: string, caminho: string, corpo?: unknown): Promise<T> {
  const resposta = await fetch(caminho, {
    method: metodo,
    credentials: 'include',
    headers: corpo === undefined ? {} : { 'content-type': 'application/json' },
    ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
  });

  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const mensagem =
      (dados as { erro?: string } | null)?.erro ?? `falha na requisição (${resposta.status})`;
    throw new ErroApi(resposta.status, mensagem, dados);
  }
  return dados as T;
}

export const api = {
  get: <T>(caminho: string): Promise<T> => pedir<T>('GET', caminho),
  post: <T>(caminho: string, corpo: unknown = {}): Promise<T> => pedir<T>('POST', caminho, corpo),
  patch: <T>(caminho: string, corpo: unknown): Promise<T> => pedir<T>('PATCH', caminho, corpo),
  /** Usado pelo upload da imagem do envio: substitui o recurso inteiro. */
  put: <T>(caminho: string, corpo: unknown): Promise<T> => pedir<T>('PUT', caminho, corpo),
  /** Sem corpo: `DELETE` com payload é recusado por proxies no caminho. */
  delete: <T>(caminho: string): Promise<T> => pedir<T>('DELETE', caminho),
};

// --- tipos do contrato -----------------------------------------------------

export type Papel = 'admin' | 'editor' | 'leitor';

export interface Capacidades {
  ler: boolean;
  editar: boolean;
  verTelefone: boolean;
  /** Recebe o CPF na resposta da API, ainda que mascarado. Leitor não recebe. */
  verCpf: boolean;
  /** Recebe o CPF inteiro. Só admin. */
  verCpfCompleto: boolean;
  administrar: boolean;
  /** Criar e disparar envio em massa, criar tags, importar contatos. */
  disparar: boolean;
}

export interface Sessao {
  usuario: { id: string; email: string; nome: string; papel: Papel };
  capacidades: Capacidades;
}

export interface FaqNaLista {
  id: string;
  categoria: string;
  pergunta: string;
  ativo: boolean;
  reindexacaoPendente: boolean;
  atualizadoEm: string;
  variacoes: number;
  trechosIndexados: number;
  vereditosPositivos: number;
  vereditosNegativos: number;
  acionamentos: number;
}

export interface ListaFaqs {
  itens: FaqNaLista[];
  total: number;
  pendentesDeReindexacao: number;
  categorias: { categoria: string; total: number }[];
  categoriaPendenteConteudo: string;
}

export interface FaqDetalhe {
  id: string;
  categoria: string;
  pergunta: string;
  resposta: string;
  variacoes: string[];
  ativo: boolean;
  reindexacaoPendente: boolean;
  atualizadoEm: string;
  trechosIndexados: number;
  trechosSemEmbedding: string[];
}

export interface ResultadoReindexacao {
  faqsProcessadas: number;
  faqsComErro: number;
  embeddingsCriados: number;
  embeddingsRemovidos: number;
  pendentesDeReindexacao: number;
}

// --- fila de lacunas -------------------------------------------------------

export type StatusLacuna = 'pendente' | 'respondida' | 'descartada';

export interface CandidataDaLacuna {
  faqId: string;
  pergunta: string | null;
  categoria: string | null;
  ocorrencias: number;
  melhorCosseno: number;
  ativa: boolean | null;
}

export interface LacunaDoGrupo {
  id: string;
  pergunta: string;
  melhorScore: number | null;
  criadoEm: string;
}

export interface GrupoDeLacunas {
  id: string;
  pergunta: string;
  ocorrencias: number;
  primeiraEm: string;
  ultimaEm: string;
  melhorScore: number | null;
  lacunas: LacunaDoGrupo[];
  candidatas: CandidataDaLacuna[];
}

export interface ListaLacunas {
  grupos: GrupoDeLacunas[];
  totalLacunas: number;
  porStatus: Partial<Record<StatusLacuna, number>>;
}

export interface Colisao {
  trecho: string;
  faqId: string;
  pergunta: string;
}

export interface ResultadoLacuna {
  ok: true;
  faq: FaqDetalhe;
  lacunasAfetadas: number;
}

export interface ResultadoDescarte {
  ok: true;
  lacunasAfetadas: number;
}

/**
 * Mensagem para o curador a partir de um erro da API.
 *
 * A colisão de trecho é a única que precisa de detalhe: sem saber QUAL
 * variação bate com QUAL FAQ, não há como corrigir — só tentar de novo às
 * cegas.
 */
export function explicarErro(e: unknown, padrao: string): string {
  if (!(e instanceof ErroApi)) return padrao;
  if (e.status === 409) {
    const colisoes = (e.detalhes as { colisoes?: Colisao[] } | null)?.colisoes ?? [];
    const lista = colisoes.map((c) => `“${c.trecho}” já é de “${c.pergunta}”`).join('; ');
    return lista ? `Trecho repetido: ${lista}.` : 'Trecho repetido em outra FAQ.';
  }
  if (e.message === 'lacunas_nao_encontradas') {
    return 'Essas lacunas já foram resolvidas por outra pessoa. Recarregue a fila.';
  }
  if (e.message === 'faq_nao_encontrada') return 'A FAQ escolhida não existe mais.';
  return e.message || padrao;
}

// --- atendimentos ----------------------------------------------------------

export type Desfecho = 'resolvido_bot' | 'transbordo' | 'abandono' | 'em_aberto';
export type Modo = 'live' | 'shadow';

/** `whatsapp` = nome do perfil, que ninguém conferiu. */
export type OrigemDoNome = 'contato' | 'whatsapp';

export interface AtendimentoNaLista {
  id: string;
  protocolo: string;
  /** Sempre mascarado. O número completo só existe no detalhe. */
  telefoneMascarado: string | null;
  nome: string | null;
  nomeOrigem: OrigemDoNome | null;
  tipoContato: string | null;
  abertoEm: string;
  fechadoEm: string | null;
  duracaoSegundos: number | null;
  desfecho: Desfecho;
  modo: Modo;
  eventos: number;
  acertos: number;
  falhas: number;
  transbordo: boolean;
  bloqueioPagamento: boolean;
}

export interface ListaAtendimentos {
  itens: AtendimentoNaLista[];
  total: number;
  podeBuscarPorTelefone: boolean;
}

export interface CandidatoNoEvento {
  rotulo?: string;
  faqId?: string;
  pergunta?: string;
  trecho?: string;
  cosseno?: number;
  rrf?: number;
}

export interface PassoDaConversa {
  id: string;
  tipo: string;
  em: string;
  deNo: string | null;
  paraNo: string | null;
  entrada: string | null;
  faixa: 'direto' | 'llm' | 'lacuna' | null;
  via: string | null;
  faqId: string | null;
  faqPergunta: string | null;
  faqCategoria: string | null;
  cosseno: number | null;
  cossenoDoTopo: number | null;
  melhorCosseno: number | null;
  motivo: string | null;
  opcaoEscolhida: string | null;
  feedback: 'positivo' | 'negativo' | null;
  mensagens: string[];
  /** `true` = texto reconstruído da FAQ de hoje, não o que foi enviado. */
  respostaEhAtual: boolean;
  trace: string[];
  candidatos: CandidatoNoEvento[];
}

export interface HandoffNoDetalhe {
  id: string;
  motivo: string;
  iniciadoEm: string;
  encerradoEm: string | null;
  encerradoPor: string | null;
}

export interface AtendimentoDetalhe {
  id: string;
  protocolo: string;
  modo: Modo;
  desfecho: Desfecho;
  abertoEm: string;
  fechadoEm: string | null;
  instancia: string;
  nome: string | null;
  nomeOrigem: OrigemDoNome | null;
  pushName: string | null;
  tipoContato: string | null;
  perfil: string | null;
  telefone: string | null;
  telefoneCompleto: boolean;
  passos: PassoDaConversa[];
  handoffs: HandoffNoDetalhe[];
  nadaFoiEnviado: boolean;
  /** `false` = atendimento anterior ao registro do texto enviado. */
  temRegistroDeEnvio: boolean;
}

// --- métricas --------------------------------------------------------------

export interface TotaisMetricas {
  rodadas: number;
  respondidas: number;
  lacunas: number;
  bloqueiosPagamento: number;
  atendimentos: number;
}

export interface VereditosMetricas {
  positivos: number;
  negativos: number;
  indefinidos: number;
  removidos: number;
  semReacao: number;
  precisao: number | null;
}

export interface FaqAcionada {
  faqId: string;
  categoria: string | null;
  pergunta: string | null;
  acionamentos: number;
  positivos: number;
  negativos: number;
  indefinidos: number;
}

export interface FaixaDoHistograma {
  de: number;
  ate: number;
  positivos: number;
  negativos: number;
  precisao: number | null;
  zona: 'abaixo_low' | 'entre' | 'acima_high';
}

export interface Histograma {
  faixas: FaixaDoHistograma[];
  amostras: number;
  limiteBaixo: number;
  limiteAlto: number;
}

export interface Metricas {
  desde: string;
  modo: string;
  totais: TotaisMetricas;
  vereditos: VereditosMetricas;
  faqs: FaqAcionada[];
  histograma: Histograma;
  desfechos: { desfecho: string; total: number; fracao: number }[];
  transbordoPorNo: {
    no: string;
    categoria: string | null;
    transbordos: number;
    atendimentosNoNo: number;
    taxa: number;
  }[];
  transbordoPorMotivo: { motivo: string; total: number; fracao: number }[];
  taxaTransbordoGeral: number | null;
}

// --- fila de transbordos ---------------------------------------------------

export type StatusTransbordo = 'pendente' | 'atendido';

export interface TransbordoNaFila {
  atendimentoId: string;
  protocolo: string;
  nome: string | null;
  nomeOrigem: OrigemDoNome | null;
  tipo: string | null;
  curso: string | null;
  anac: string | null;
  noSaga: boolean;
  telefoneMascarado: string | null;
  abertoEm: string;
  transbordouEm: string;
  motivo: string;
  pergunta: string | null;
  /** Opção de menu escolhida, em rótulos legíveis. */
  opcaoEscolhida: string | null;
  melhorScore: number | null;
  detalhe: string | null;
  modo: string;
  status: StatusTransbordo;
  atendidoEm: string | null;
  atendidoPorNome: string | null;
  observacao: string | null;
  /** Minutos de ATENDIMENTO (não de parede) desde o transbordo. */
  minutosUteis: number;
  atrasado: boolean;
}

export interface ListaTransbordos {
  itens: TransbordoNaFila[];
  pendentes: number;
  atendidos: number;
}

export interface Pendencias {
  transbordos: number;
  lacunas: number;
  reindexacao: number;
  /** Conversas com o bot calado. Ver `conversasDesconhecidas`. */
  conversasPausadas: number;
  /** Sessões vivas agora — alimenta o bloco "agora" da lateral. */
  conversasAtivas: number;
  /** Redis fora: os dois números de conversa são desconhecidos, não zero. */
  conversasDesconhecidas: boolean;
}

// --- usuários --------------------------------------------------------------

export interface UsuarioNaLista {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
  ativo: boolean;
  criadoEm: string;
  ultimoAcesso: string | null;
  /** Nunca definiu senha: o convite ainda não foi aceito. */
  semSenha: boolean;
  convitePendente: boolean;
  sessoesAtivas: number;
}

export interface ListaUsuarios {
  itens: UsuarioNaLista[];
  /** Id de quem está logado: a tela não oferece o que o servidor vai recusar. */
  eu: string;
  senhaMinima: number;
}

export interface ConviteGerado {
  /** Token em claro. Chega UMA vez; o banco guarda só o hash. */
  token: string;
  expiraEm: string;
  usuarioId: string;
}

export interface UsuarioCriado {
  usuario: UsuarioNaLista;
  convite: ConviteGerado;
}

export interface ConvitePronto {
  nome: string;
  email: string;
  primeiroAcesso: boolean;
}

/** Mensagens das recusas do servidor, em português de gente. */
export const MOTIVOS: Record<string, string> = {
  email_duplicado: 'Já existe um usuário com este e-mail.',
  email_invalido: 'E-mail inválido.',
  papel_invalido: 'Papel inválido.',
  ultimo_admin: 'Este é o último admin ativo. O painel ficaria sem ninguém que possa administrá-lo.',
  proprio_papel: 'Ninguém altera o próprio papel — pedir a outro admin evita um erro irreversível.',
  proprio_status: 'Ninguém desativa a si mesmo.',
  senha_fraca: 'A senha não atende à política.',
  senha_atual_incorreta: 'A senha atual está incorreta.',
  convite_invalido: 'Este convite não é mais válido.',
  usuario_inativo: 'Usuário desativado: reative antes de enviar um convite.',
  nao_encontrado: 'Não encontrado.',
};

export function explicarMotivo(e: unknown, padrao: string): string {
  if (!(e instanceof ErroApi)) return padrao;
  const detalhes = e.detalhes as { problemas?: string[] } | null;
  const problemas = detalhes?.problemas;
  if (problemas && problemas.length > 0) return `A senha ${problemas.join('; ')}.`;
  return MOTIVOS[e.message] ?? e.message ?? padrao;
}

// --- conversas (estado do bot) ---------------------------------------------

export type EstadoBot = 'bot' | 'humano' | 'pausado_auto';

export interface ConversaAtiva {
  /**
   * O JID (telefone completo) NÃO vem do servidor: as ações são endereçadas
   * pelo `atendimentoId`, senão a máscara da lista seria decorativa.
   */
  telefoneMascarado: string | null;
  nome: string | null;
  nomeOrigem: OrigemDoNome | null;
  estado: EstadoBot;
  estadoDesde: string;
  ultimaInteracao: string;
  expiraEm: string | null;
  no: string;
  trilha: string[];
  protocolo: number | null;
  atendimentoId: string;
  aguardando: string | null;
}

export interface ListaConversas {
  itens: ConversaAtiva[];
  pausadas: number;
}

/**
 * Rótulos em linguagem de OPERAÇÃO, não de código.
 *
 * `pausado_auto` não diz nada a quem está no balcão; "Secretaria assumiu pelo
 * WhatsApp" diz o que aconteceu e por que o bot está calado.
 */
export const ROTULO_ESTADO: Record<EstadoBot, string> = {
  bot: 'Bot respondendo',
  humano: 'Com a secretaria',
  pausado_auto: 'Secretaria assumiu pelo WhatsApp',
};

export const EXPLICACAO_ESTADO: Record<EstadoBot, string> = {
  bot: 'o aluno recebe resposta automática normalmente',
  humano: 'o bot está calado: alguém pediu atendimento humano',
  pausado_auto: 'o bot se calou porque alguém respondeu pelo WhatsApp Web',
};

// --- histórico contínuo da conversa ----------------------------------------

/**
 * O fio da conversa, agrupado por TELEFONE.
 *
 * Espelha `src/core/painel/historico.ts`. Datas chegam como string ISO, como
 * no resto deste contrato.
 */
export interface DetalheDaResposta {
  faqId: string | null;
  faqPergunta: string | null;
  faqCategoria: string | null;
  faixa: 'direto' | 'llm' | 'lacuna' | null;
  via: string | null;
  cossenoDoTopo: number | null;
  motivo: string | null;
  /** 👍/👎 que o ALUNO digitou na pergunta de feedback. */
  feedbackDoAluno: 'positivo' | 'negativo' | null;
  /** Reação da secretaria no relatório de sombra. */
  vereditoDaSecretaria: 'positivo' | 'negativo' | null;
}

export interface ItemAtendimento {
  tipo: 'atendimento';
  id: string;
  em: string;
  atendimentoId: string;
  protocolo: string;
  modo: Modo;
  desfecho: Desfecho;
}

export interface ItemMensagem {
  tipo: 'mensagem';
  id: string;
  em: string;
  autor: 'aluno' | 'bot';
  /** `null` = o bot respondeu e o texto não foi gravado. */
  texto: string | null;
  /** `false` em sombra: nada chegou ao aluno. */
  enviado: boolean;
  /** `true` quando o texto é a resposta ATUAL da FAQ, não o literal da época. */
  textoAtualDaFaq: boolean;
  detalhe: DetalheDaResposta | null;
}

export interface ItemSistema {
  tipo: 'sistema';
  id: string;
  em: string;
  texto: string;
  classe: 'transbordo' | 'pausa' | 'retomada' | 'sessao' | 'bloqueio';
}

export type ItemDaConversa = ItemAtendimento | ItemMensagem | ItemSistema;

export interface ConversaCompleta {
  atendimentoId: string;
  nome: string | null;
  nomeOrigem: OrigemDoNome | null;
  pushName: string | null;
  tipoContato: string | null;
  perfil: string | null;
  telefone: string | null;
  telefoneCompleto: boolean;
  itens: ItemDaConversa[];
  atendimentosCarregados: number;
  temMaisAntigos: boolean;
  cursor: string | null;
  totalAtendimentos: number;
}

// --- tela inicial ----------------------------------------------------------

/**
 * Espelha `src/core/painel/home.ts`.
 *
 * Os campos de suficiência (`suficienteParaTendencia`, `amostraSuficiente`)
 * fazem parte do contrato de propósito: a base vive quase vazia por semanas, e
 * a tela não deve adivinhar se já pode desenhar uma tendência.
 */
export interface MaisAntigoPendente {
  atendimentoId: string;
  protocolo: string;
  nome: string | null;
  transbordouEm: string;
  minutosUteis: number;
  atrasado: boolean;
}

export interface PrecisaDeMim {
  transbordosPendentes: number;
  maisAntigo: MaisAntigoPendente | null;
  conversasPausadas: number;
  /** Redis fora: o número é desconhecido, não zero. */
  conversasDesconhecidas: boolean;
  lacunasPendentes: number;
  faqsPendentesDeReindexacao: number;
}

export interface PeriodoDaHome {
  mensagens: number;
  respondidas: number;
  transbordos: number;
  atendimentos: number;
  taxaResolucao: number | null;
  taxaTransbordo: number | null;
}

export interface DiaDaSerie {
  /** `YYYY-MM-DD` no fuso do aeroclube. */
  dia: string;
  mensagens: number;
  respondidas: number;
  transbordos: number;
}

export interface VolumeDaHome {
  serie: DiaDaSerie[];
  diasComDados: number;
  suficienteParaTendencia: boolean;
  diasParaTendencia: number;
}

export interface HoraDoDia {
  hora: number;
  mensagens: number;
}

export interface HorarioDaHome {
  horas: HoraDoDia[];
  total: number;
  foraDoHorarioComercial: number;
  fracaoForaDoHorario: number | null;
  amostraSuficiente: boolean;
  amostraMinima: number;
}

export interface IndoMal {
  faqsComVereditoNegativo: FaqAcionada[];
  transbordoPorNo: {
    no: string;
    categoria: string | null;
    transbordos: number;
    atendimentosNoNo: number;
    taxa: number;
  }[];
  transbordoPorMotivo: { motivo: string; total: number; fracao: number }[];
  negativos: number;
}

export interface Home {
  desde: string;
  dias: number;
  modo: string;
  precisaDeMim: PrecisaDeMim;
  periodo: PeriodoDaHome;
  volume: VolumeDaHome;
  horario: HorarioDaHome;
  indoMal: IndoMal;
}

// --- contatos (coluna esquerda das conversas) ------------------------------

/** Espelha `src/core/painel/contatos.ts`. */
export interface ContatoNaLista {
  /** Atendimento mais recente deste telefone. É o endereço da conversa. */
  atendimentoId: string;
  protocolo: string;
  nome: string | null;
  nomeOrigem: OrigemDoNome | null;
  /** Do SAGA. `null` quando a pessoa não está lá. */
  tipo: string | null;
  curso: string | null;
  /** `false` = não está no SAGA. Provável interessado. */
  noSaga: boolean;
  /** SEMPRE mascarado. O completo só no cabeçalho da conversa. */
  telefoneMascarado: string | null;
  ultimaMensagem: string | null;
  ultimoAutor: 'aluno' | 'bot' | null;
  ultimaEm: string;
  atendimentos: number;
  /** `null` = sem sessão, ou estado desconhecido (ver `estadoDesconhecido`). */
  estado: EstadoBot | null;
  modo: string;
}

export interface ListaDeContatos {
  itens: ContatoNaLista[];
  cursor: string | null;
  temMais: boolean;
  /** Redis fora: os indicadores de estado são desconhecidos, não "bot ativo". */
  estadoDesconhecido: boolean;
  /** Busca por protocolo que casou — a tela rola o fio até ele. */
  atendimentoEncontrado: { atendimentoId: string; protocolo: string } | null;
  podeBuscarPorTelefone: boolean;
}

// --- árvore de menu --------------------------------------------------------

/** Espelha `src/core/painel/menu.ts`. */
export type StatusVersaoMenu = 'rascunho' | 'publicada' | 'arquivada';

/** A FAQ que uma opção responde. Espelha `FaqDoDestino` no servidor. */
export interface FaqDoDestino {
  /** `null` quando o slug não casa com nenhuma linha da base. */
  id: string | null;
  pergunta: string;
  categoria: string | null;
  ativo: boolean;
  reindexacaoPendente: boolean;
  /** Categoria de encaminhamento: na prática o item de menu transborda. */
  encaminhaParaSecretaria: boolean;
  /** Slug ou id que não resolve: a opção quebra na publicação. */
  ausente: boolean;
}

export interface CategoriaDoDestino {
  nome: string;
  /** Zero significa que toda pergunta ali vira lacuna. */
  faqsAtivas: number;
}

export interface OpcaoDoMenu {
  id: string;
  label: string;
  /** Para onde leva. Etapa 1 não edita — a tela só mostra. */
  proximoNo: string | null;
  action: string | null;
  /** FAQs respondidas por esta opção. Um slug pode apontar para mais de uma. */
  faqs: FaqDoDestino[];
  /** Preenchida quando a opção abre pergunta livre filtrada. */
  categoria: CategoriaDoDestino | null;
  /**
   * Texto que a opção envia e que vive no CÓDIGO. `null` = nada trancado.
   *
   * A tela usa isto para dizer o que a secretaria NÃO controla ali: o rótulo
   * muda, o texto enviado não.
   */
  textoNoCodigo: string | null;
  tipoDeDestino: 'no' | 'transbordo' | 'faq_direta' | 'duvidas' | 'acao';
  destinoLegivel: string;
  ordem: number;
  /** Identificador de regra de visibilidade. `null` = aparece sempre. */
  visivelRegra: string | null;
  /** Número que o aluno digita. `null` quando depende do contexto. */
  numero: string | null;
  caracteres: number;
  rotuloLongo: boolean;
}

export interface NoDoMenu {
  id: string;
  noId: string;
  texto: string;
  textoSemAssunto: string | null;
  saudacao: string | null;
  saudacaoSemNome: string | null;
  textoForaHorario: string | null;
  textoProximaAbertura: string | null;
  marcadores: string[];
  ordem: number;
  opcoes: OpcaoDoMenu[];
  vagasRestantes: number;
  categoriaDaBusca: string | null;
  paisDiretos: string[];
  ehRaiz: boolean;
  /** `false` = sai da árvore na próxima publicação; o registro fica. */
  ativo: boolean;
}

export interface VersaoDoMenu {
  id: string;
  numero: number;
  status: StatusVersaoMenu;
  nota: string | null;
  criadaEm: string;
  publicadaEm: string | null;
  publicadaPorNome: string | null;
  alteradaEm: string | null;
  alteradaPorNome: string | null;
}

export interface ArvoreDoMenu {
  versao: VersaoDoMenu;
  nos: NoDoMenu[];
  reservado: { numero: string; label: string };
  limites: { maxOpcoes: number; limiteRotulo: number };
}

export interface EstadoDoMenu {
  publicada: ArvoreDoMenu | null;
  /** `null` = ninguém está editando. */
  rascunho: ArvoreDoMenu | null;
  /**
   * Problemas de estrutura do rascunho — os MESMOS que bloqueiam a publicação.
   * Aqui são aviso: a pessoa vê enquanto trabalha em vez de descobrir no botão.
   */
  problemas: ProblemaDoMenu[];
}

export type MudancaDoMenu =
  | { tipo: 'texto'; noId: string; campo: string; antes: string | null; depois: string | null }
  | { tipo: 'rotulo'; noId: string; antes: string; depois: string; destinoLegivel: string }
  | { tipo: 'ordem'; noId: string; antes: string[]; depois: string[] }
  | { tipo: 'no_criado' | 'no_desativado' | 'no_reativado'; noId: string; detalhe: string }
  | {
      tipo: 'opcao_criada' | 'opcao_removida';
      noId: string;
      label: string;
      destinoLegivel: string;
    }
  | { tipo: 'destino_trocado'; noId: string; label: string; antes: string; depois: string }
  | { tipo: 'opcao_movida'; noId: string; label: string; de: string; para: string };

/** Tipos que mexem na FORMA da árvore. O resumo mostra estes primeiro. */
export const TIPOS_ESTRUTURAIS: readonly MudancaDoMenu['tipo'][] = [
  'no_criado',
  'no_desativado',
  'no_reativado',
  'opcao_criada',
  'opcao_removida',
  'destino_trocado',
  'opcao_movida',
];

export function ehMudancaEstrutural(m: MudancaDoMenu): boolean {
  return TIPOS_ESTRUTURAIS.includes(m.tipo);
}

export interface DiferencasDoMenu {
  mudancas: MudancaDoMenu[];
  rascunhoId: string | null;
  publicadaNumero: number | null;
}

export interface VersaoNoHistorico {
  id: string;
  numero: number;
  status: StatusVersaoMenu;
  nota: string | null;
  criadaEm: string;
  publicadaEm: string | null;
  publicadaPorNome: string | null;
  nos: number;
  opcoes: number;
}

/** Campos de texto editáveis de um nó. Lista fechada: ver a etapa 1. */
export type CampoDeTextoMenu =
  | 'texto'
  | 'textoSemAssunto'
  | 'saudacao'
  | 'saudacaoSemNome'
  | 'textoForaHorario'
  | 'textoProximaAbertura';

// --- estrutura da árvore (etapa 2) -----------------------------------------

/** Para onde uma opção leva. Espelha `Destino` em `menuEstrutura.ts`. */
export type DestinoDeOpcao =
  | { tipo: 'no'; proximoNo: string }
  | { tipo: 'no_novo'; noId: string; texto: string }
  | { tipo: 'faq'; faqId: string }
  | { tipo: 'duvidas'; categoria: string }
  | { tipo: 'transbordo' };

export interface FaqParaMenu {
  id: string;
  pergunta: string;
  categoria: string;
  reindexacaoPendente: boolean;
  /** Categoria de encaminhamento: escolher aqui é escolher um transbordo. */
  encaminhaParaSecretaria: boolean;
}

export interface CategoriaComContagem {
  categoria: string;
  /** Zero significa que toda pergunta ali viraria lacuna. */
  faqsAtivas: number;
}

export interface DestinosDisponiveis {
  nos: string[];
  faqs: FaqParaMenu[];
  /** Lista FECHADA: categoria inexistente filtraria a busca para zero. */
  categorias: CategoriaComContagem[];
}

export type TipoDeProblemaDoMenu =
  | 'orfao'
  | 'ciclo'
  | 'destino_inexistente'
  | 'destino_desativado'
  | 'faq_inativa'
  | 'acima_do_teto'
  | 'sem_opcoes';

export interface ProblemaDoMenu {
  tipo: TipoDeProblemaDoMenu;
  noId: string;
  mensagem: string;
  /** O que fazer para resolver. Sem isto a mensagem abandona a pessoa. */
  oQueFazer: string;
}

export interface CaminhoDeEntrada {
  opcaoId: string;
  paiNoId: string;
  label: string;
}

export interface TrafegoDoNo {
  /** Atendimentos distintos que passaram pelo nó na janela. */
  atendimentos: number;
  dias: number;
}

export interface TrafegoEEntradas {
  trafego: TrafegoDoNo;
  entradas: CaminhoDeEntrada[];
}

// --- pessoas (espelho do SAGA) ---------------------------------------------

export interface PessoaNaLista {
  id: string;
  sagaId: string | null;
  nome: string | null;
  apelido: string | null;
  tipo: string;
  curso: string | null;
  /** Completo para admin e editor, mascarado para leitor. `null` = não tem. */
  telefone: string | null;
  /** Motivo por extenso, ou `null` quando o telefone existe. */
  telefoneMotivo: string | null;
  email: string | null;
  anac: string | null;
  nascimento: string | null;
  /** AUSENTE para leitor: a chave não vem no JSON. */
  cpf?: string | null;
  ativo: boolean;
  /** Telefone que a autoidentificação revelou e que o SAGA não tem. */
  telefoneDescoberto: string | null;
  telefoneDescobertoEm: string | null;
}

export interface ResumoPessoas {
  total: number;
  alunos: number;
  instrutores: number;
  administrativos: number;
  reconhecidos: number;
  semTelefone: { motivo: string; rotulo: string; total: number }[];
  /** Telefones revelados pela autoidentificação e ausentes no SAGA. */
  telefonesDescobertos: number;
}

export interface EstadoSincronizacao {
  iniciadaEm: string;
  terminadaEm: string | null;
  ok: boolean;
  origem: string;
  recebidos: number;
  criados: number;
  atualizados: number;
  desativados: number;
  comTelefone: number;
  tiposDesconhecidos: string[];
  erro: string | null;
}

export interface ListaPessoas {
  itens: PessoaNaLista[];
  total: number;
  resumo: ResumoPessoas;
  ultimaSincronizacao: EstadoSincronizacao | null;
  sincronizacaoAtiva: boolean;
  tipos: string[];
}

// --- envios em massa -------------------------------------------------------

/**
 * Espelha `src/core/envio/*`.
 *
 * Datas chegam como string ISO, como no resto deste contrato.
 */
export type TipoDeEnvio = 'servico' | 'campanha';

export type EstadoDoEnvio =
  | 'rascunho'
  | 'agendado'
  | 'enviando'
  | 'pausado'
  | 'concluido'
  | 'cancelado';

/** Estado de um destinatário. `pulado` carrega o motivo da exclusão. */
export type EstadoDestinatario =
  | 'pendente'
  | 'enviando'
  | 'enviado'
  | 'entregue'
  | 'lido'
  | 'falhou'
  | 'adiado'
  | 'pulado';

export type MotivoDeExclusao =
  | 'opt_out'
  | 'sem_telefone'
  | 'telefone_invalido'
  | 'duplicado'
  | 'variavel_vazia';

export const VARIAVEIS_DE_ENVIO = ['nome', 'apelido', 'curso', 'canac', 'tipo'] as const;
export type VariavelDeEnvio = (typeof VARIAVEIS_DE_ENVIO)[number];

/**
 * Rótulos em linguagem de OPERAÇÃO.
 *
 * `pulado` não diz nada a quem está no balcão; "não recebe" diz, e o motivo ao
 * lado explica por quê. É a mesma escolha de `ROTULO_ESTADO` das conversas.
 */
export const ROTULO_ESTADO_ENVIO: Record<EstadoDoEnvio, string> = {
  rascunho: 'Rascunho',
  agendado: 'Agendado',
  enviando: 'Enviando',
  pausado: 'Pausado',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

export const COR_ESTADO_ENVIO: Record<EstadoDoEnvio, string> = {
  rascunho: 'gray',
  agendado: 'blue',
  enviando: 'teal',
  pausado: 'orange',
  concluido: 'green',
  cancelado: 'red',
};

/**
 * Motivo gravado -> frase para quem está no balcão.
 *
 * A coluna `motivo` guarda identificador (e, nas falhas de rede, a mensagem
 * crua da Evolution). `em_atendimento_prolongado` sem tradução não diz a nada
 * a quem lê — e é justamente o motivo acionável: alguém tem uma conversa
 * aberta há mais de um dia e aquela pessoa não recebeu o aviso.
 */
export const MOTIVOS_DESTINATARIO: Record<string, string> = {
  opt_out: 'pediu para não receber',
  sem_telefone: 'sem telefone utilizável no cadastro',
  telefone_invalido: 'telefone inválido',
  duplicado: 'número repetido na mesma lista',
  variavel_vazia: 'faltou dado para uma variável do texto',
  em_atendimento: 'em atendimento com a secretaria — será retentado',
  em_atendimento_prolongado:
    'em atendimento há mais de um dia: o envio não ficou esperando mais',
  envio_cancelado: 'o envio foi cancelado antes de chegar nesta pessoa',
  sem_telefone_ou_texto_na_montagem: 'a linha ficou sem telefone ou sem texto na montagem',
};

/**
 * Traduz o motivo. O que não está no mapa sai CRU: é a mensagem de erro da
 * Evolution, e inventar uma frase amigável no lugar dela esconderia a única
 * pista de por que aquela mensagem não saiu.
 */
export function explicarMotivoDestinatario(motivo: string | null): string | null {
  if (!motivo) return null;
  const direto = MOTIVOS_DESTINATARIO[motivo];
  if (direto) return direto;
  // `variavel_vazia: {curso}` — o prefixo traduz, o detalhe acompanha.
  const [chave, ...resto] = motivo.split(': ');
  const porPrefixo = chave ? MOTIVOS_DESTINATARIO[chave] : undefined;
  if (porPrefixo) return resto.length > 0 ? `${porPrefixo} (${resto.join(': ')})` : porPrefixo;
  return motivo;
}

export const ROTULO_DESTINATARIO: Record<EstadoDestinatario, string> = {
  pendente: 'na fila',
  enviando: 'saindo',
  enviado: 'enviado',
  entregue: 'entregue',
  lido: 'lido',
  falhou: 'falhou',
  adiado: 'adiado',
  pulado: 'não recebe',
};

export interface ContagensDoEnvio {
  total: number;
  pendentes: number;
  /** CUMULATIVO: inclui entregues e lidos. Ver a nota no servidor. */
  enviados: number;
  entregues: number;
  lidos: number;
  falhas: number;
  adiados: number;
  pulados: number;
  /** 0 a 1. `null` quando a lista está vazia. */
  progresso: number | null;
}

export interface LimitesDeEnvio {
  tetoDiario: number;
  pausaMinSeg: number;
  pausaMaxSeg: number;
  janelaInicio: number;
  janelaFim: number;
  /** Prazo máximo de um destinatário adiado por atendimento, em horas. */
  adiadoTetoHoras: number;
}

export interface EstadoDoTeto {
  teto: number;
  usadasHoje: number;
  restante: number;
  atingido: boolean;
  instancia: string;
}

export interface ContextoDeEnvios {
  /** `false` = `ENVIO_ATIVO=false`: a tela fica em leitura e iniciar recusa. */
  ativo: boolean;
  /**
   * `shadow` | `live` — o modo do BOT DE ATENDIMENTO.
   *
   * O envio em massa NÃO o respeita: em sombra o bot fica calado, mas um
   * disparo sai de verdade. A tela avisa, porque a suposição contrária é
   * natural e caríssima.
   */
  modoDoBot: string;
  /** O disparo sai do MESMO número do atendimento? Decide o aviso de risco. */
  numeroCompartilhado: boolean;
  instanciaPadrao: string;
  limites: LimitesDeEnvio;
  teto: EstadoDoTeto;
  /** `null` = a janela está aberta agora. Senão, "amanhã às 08:00". */
  janelaFechadaAte: string | null;
  rodapeCampanha: string;
}

export interface EnvioNaLista {
  id: string;
  nome: string;
  tipo: TipoDeEnvio;
  estado: EstadoDoEnvio;
  temImagem: boolean;
  criadoEm: string;
  criadoPorNome: string | null;
  inicioEm: string | null;
  iniciadoEm: string | null;
  concluidoEm: string | null;
  /** `null` = nunca testado. Iniciar é recusado nesse caso. */
  testeEm: string | null;
  instancia: string;
  contagens: ContagensDoEnvio;
}

export interface ListaDeEnvios {
  itens: EnvioNaLista[];
  contexto: ContextoDeEnvios;
}

export interface DestinatarioNaTela {
  contatoId: string;
  nome: string | null;
  /** Completo para quem tem `verTelefone`; mascarado para leitor. */
  telefone: string | null;
  estado: EstadoDestinatario;
  motivo: string | null;
  tentativas: number;
  enviadoEm: string | null;
  entregueEm: string | null;
  lidoEm: string | null;
}

export interface EnvioDetalhe extends EnvioNaLista {
  texto: string;
  imagemCaminho: string | null;
  imagemMime: string | null;
  pausaMinSeg: number;
  pausaMaxSeg: number;
  testePara: string | null;
  canceladoEm: string | null;
  canceladoPorNome: string | null;
  /** `disparo_desligado` = o laço pausou sozinho. `null` = pausa manual. */
  pausaMotivo: string | null;
  numeroCompartilhado: boolean;
  destinatarios: DestinatarioNaTela[];
  exclusoes: { motivo: string; rotulo: string; total: number }[];
}

export interface ProblemaDeTexto {
  marcador: string;
  tipo: 'proibida' | 'desconhecida';
  mensagem: string;
}

export interface ValidacaoDeTexto {
  ok: boolean;
  usadas: VariavelDeEnvio[];
  problemas: ProblemaDeTexto[];
}

export interface ExcluidoDaLista {
  contatoId: string;
  nome: string | null;
  telefoneMascarado: string | null;
  motivo: MotivoDeExclusao;
  faltando?: VariavelDeEnvio[];
}

export interface ResumoDeExclusao {
  motivo: MotivoDeExclusao;
  rotulo: string;
  total: number;
  exemplos: { nome: string | null; telefoneMascarado: string | null }[];
}

/** O que a tela mostra ANTES de o envio existir. Ver `POST /admin/envios/previa`. */
export interface PreviaDeEnvio {
  total: number;
  candidatos: number;
  exclusoes: ResumoDeExclusao[];
  texto: ValidacaoDeTexto;
  semVariavel: ExcluidoDaLista[];
  /** Renderizado com uma pessoa REAL da lista. `null` = lista vazia. */
  exemplo: { nome: string | null; texto: string } | null;
  contexto: ContextoDeEnvios;
}

export interface CriteriosDeLista {
  tipos?: string[];
  tags?: string[];
}

// --- tags ------------------------------------------------------------------

export interface TagNaLista {
  id: string;
  nome: string;
  descricao: string | null;
  criadoEm: string;
  /** Contatos ativos com a tag. */
  contatos: number;
  /** Quantos deles RECEBERIAM: com telefone e sem opt-out. */
  alcancaveis: number;
}

export interface ContatoMarcavel {
  id: string;
  nome: string | null;
  tipo: string;
  curso: string | null;
  origem: string;
  telefoneMascarado: string | null;
  /** `true` = não receberia nem entrando na tag. */
  semTelefone: boolean;
  semEnvio: boolean;
  tags: { id: string; nome: string }[];
}

export interface ListaMarcaveis {
  itens: ContatoMarcavel[];
  tipos: { tipo: string; total: number }[];
}

// --- importação de CSV -----------------------------------------------------

export interface LinhaImportada {
  linha: number;
  nome: string | null;
  telefoneBruto: string;
  e164: string | null;
  motivo: string | null;
  tags: string[];
}

export interface PreviaDeImportacao {
  resumo: { novos: number; existentes: number; invalidos: number; tagsNovas: number };
  novos: LinhaImportada[];
  existentes: LinhaImportada[];
  invalidos: LinhaImportada[];
  tags: { nome: string; nova: boolean }[];
  colunas: string[];
  erro: string | null;
}

export interface ResultadoImportacao {
  criados: number;
  atualizados: number;
  invalidos: number;
  marcacoes: number;
  tagsCriadas: number;
}

/**
 * Motivo de recusa -> frase para a secretaria.
 *
 * O servidor já manda `detalhe` com a frase; este mapa é a rede de segurança
 * para uma recusa nova que a tela ainda não conheça.
 */
export const MOTIVOS_ENVIO: Record<string, string> = {
  sem_criterios: 'Escolha pelo menos um tipo de pessoa ou uma tag.',
  texto_invalido: 'O texto usa uma variável que não existe ou que não pode sair em mensagem.',
  lista_vazia: 'Nenhuma pessoa dos critérios escolhidos pode receber.',
  variaveis_vazias:
    'Há destinatários sem valor para alguma variável do texto. Exclua-os ou reescreva o texto.',
  envio_desligado: 'O disparo em massa está desligado no ambiente (ENVIO_ATIVO=false).',
  sem_teste: 'Mande um envio de teste antes de iniciar.',
  sem_pendentes: 'Não há destinatário pendente neste envio.',
  estado_invalido: 'O envio não está num estado que permita esta ação.',
  nome_duplicado: 'Já existe uma tag com este nome.',
  nome_invalido: 'O nome não produziu um identificador válido (use letras e números).',
  telefone_invalido: 'O telefone não é um celular brasileiro utilizável.',
  cabecalho_invalido: 'O CSV precisa de um cabeçalho com a coluna "telefone".',
  arquivo_vazio: 'O CSV não tem linha de dados.',
  mime_invalido: 'Imagem em formato não aceito (use JPEG, PNG ou WebP).',
  muito_grande: 'Imagem acima do limite de 5 MB.',
  /**
   * A api não reconheceu o endereço e ele caiu numa rota com `:id`.
   *
   * O servidor já manda um `detalhe` mais específico (com o segmento que ele
   * recebeu), e `explicarEnvio` o prefere. Esta entrada é a rede para uma api
   * tão antiga que nem o `detalhe` traz — justamente o caso em que a frase
   * mais importa.
   */
  id_invalido:
    'A api não reconheceu este endereço. Se o painel foi atualizado há pouco, a api pode estar numa versão anterior — confira se os dois serviços estão no mesmo deploy.',
};

/** Resposta de `PATCH /admin/envios/:id`. */
export interface ResultadoEdicaoDeEnvio {
  ok: true;
  texto: ValidacaoDeTexto;
  /** `true` = o teste caiu e iniciar volta a ser bloqueado. */
  testeInvalidado: boolean;
  /** Presente quando os critérios foram redefinidos. */
  total?: number;
  exclusoes?: ResumoDeExclusao[];
}

/** A frase do servidor vence a nossa: ele sabe o contexto exato da recusa. */
export function explicarEnvio(e: unknown, padrao: string): string {
  if (!(e instanceof ErroApi)) return padrao;
  const detalhe = (e.detalhes as { detalhe?: string } | null)?.detalhe;
  if (detalhe) return detalhe;
  return MOTIVOS_ENVIO[e.message] ?? e.message ?? padrao;
}

// --- configuração do disparo (só admin) ------------------------------------

/**
 * Espelha `src/core/envio/configuracao.ts`.
 *
 * `origem` é o que a tela mostra como selo em cada campo: `banco` = alguém
 * definiu no painel; `ambiente` = ninguém definiu, e o valor em vigor é o da
 * variável de ambiente. O botão de limpar manda `null` e devolve o campo a
 * `ambiente`.
 */
export type OrigemDaConfiguracao = 'banco' | 'ambiente';

export type CampoDaConfiguracao =
  | 'tetoDiario'
  | 'pausaMinSeg'
  | 'pausaMaxSeg'
  | 'janelaInicio'
  | 'janelaFim'
  | 'adiadoTetoHoras'
  | 'instancia';

export interface LinhaDaConfiguracao {
  campo: CampoDaConfiguracao;
  /** O valor EM VIGOR, já resolvido pela precedência. */
  valor: number | string;
  origem: OrigemDaConfiguracao;
  /** `null` = não definido no painel. */
  noBanco: number | string | null;
  noAmbiente: number | string;
  /** `null` para `instancia`, que é texto livre. */
  faixa: { min: number; max: number } | null;
}

export interface EstadoDaConfiguracao {
  ativo: {
    valor: boolean;
    origem: OrigemDaConfiguracao;
    noBanco: boolean | null;
    noAmbiente: boolean;
  };
  campos: LinhaDaConfiguracao[];
  /** A instância do ATENDIMENTO: a padrão do bot, quando nada é definido. */
  instanciaDoAtendimento: string;
  atualizadoEm: string | null;
  atualizadoPor: string | null;
  /** Segundos até a mudança alcançar o worker. É o TTL do cache. */
  atrasoDoWorkerSegundos: number;
}

export interface ProblemaDeConfiguracao {
  campo: string;
  /** Frase pronta, com a faixa. Vem do servidor. */
  mensagem: string;
}

export interface ResultadoDaConfiguracao {
  ok: true;
  alterados: string[];
  estado: EstadoDaConfiguracao;
}

/**
 * Rótulo e explicação de cada campo.
 *
 * Moram na TELA, e não no servidor: são texto de interface, como os rótulos de
 * qualquer formulário. O que vem do servidor é o que ele é a fonte da verdade
 * sobre — o valor, a origem e a FAIXA (que é a mesma usada na validação).
 */
export const CAMPOS_DA_CONFIGURACAO: Record<
  CampoDaConfiguracao,
  { rotulo: string; unidade: string; ajuda: string }
> = {
  tetoDiario: {
    rotulo: 'Teto diário',
    unidade: 'mensagens por dia',
    ajuda:
      'Máximo de mensagens por dia e por número, somando todos os envios. Batido o teto, o envio para e continua amanhã de onde estava.',
  },
  pausaMinSeg: {
    rotulo: 'Pausa mínima',
    unidade: 'segundos',
    ajuda: 'Menor intervalo entre duas mensagens. O valor real é sorteado entre a mínima e a máxima.',
  },
  pausaMaxSeg: {
    rotulo: 'Pausa máxima',
    unidade: 'segundos',
    ajuda: 'Maior intervalo entre duas mensagens. Intervalo sorteado, nunca fixo: cadência constante é a assinatura mais óbvia de robô.',
  },
  janelaInicio: {
    rotulo: 'Janela abre às',
    unidade: 'hora',
    ajuda: 'Antes disso nada sai. Vale de segunda a sábado; domingo nunca.',
  },
  janelaFim: {
    rotulo: 'Janela fecha às',
    unidade: 'hora',
    ajuda: 'Depois disso o envio para e retoma na próxima abertura, sem perder ninguém.',
  },
  adiadoTetoHoras: {
    rotulo: 'Prazo do adiado',
    unidade: 'horas de janela',
    ajuda:
      'Quem está em atendimento com a secretaria é adiado e retentado. Passado este prazo, passa a "falhou" e deixa de segurar o envio. São horas de JANELA ABERTA, não de calendário.',
  },
  instancia: {
    rotulo: 'Instância do disparo',
    unidade: '',
    ajuda:
      'Nome da instância da Evolution por onde o disparo sai. Vazio = a mesma do bot de atendimento. A URL e a chave continuam só em variável de ambiente.',
  },
};

/** Recusa de faixa, por campo. A frase vem do servidor. */
export function problemasPorCampo(e: unknown): Record<string, string> {
  if (!(e instanceof ErroApi)) return {};
  const detalhes = e.detalhes as { problemas?: ProblemaDeConfiguracao[] } | null;
  const problemas = detalhes?.problemas ?? [];
  return Object.fromEntries(problemas.map((p) => [p.campo, p.mensagem]));
}
